import { useEffect, useState } from "react";
import { cellWidth } from "../../../../core/methods/tableCellWidth";
import {
  parseDate,
  safeLocaleCompare,
} from "../../../../core/methods/sorterMethods";
import { ColumnsType } from "antd/es/table";
import Datatable from "../../../../core/common/dataTable";
import { PageBreadcrumb } from "../../../../core/common/BreadCrumbForm";
import { all_routes } from "../../../router/all_routes";
import useApi from "../../../../core/customhook/useApi";
import webservice_url from "../../../../core/webservice/Webservice_url";
import { PaginationType } from "../../../../types/common-type";
import UserDetailsSection from "../UserDetailsSection";
import useLocalStorage from "../../../../core/customhook/hooks/useLocalStorage";
import Loading from "../../../../core/common/custom-loading/Loading";
import { useParams } from "react-router";
import { formattedDate } from "../../../../core/methods/formattedDate";
import { toast } from "react-toastify";
import DynamicSingleSelect, {
  FilterTypes,
  FilterErrors,
  initialFilters,
} from "../../reusable/DynamicSingleSelect";

const breadcrumbItem = [
  { label: "Manage Verifier" },
  { label: "Manage Verifier Cases" },
];

const Admin_ManageVerifierCases = () => {
  const { verifierId } = useParams();
  const { getLocalStorage } = useLocalStorage();

  // ================= STATES =================

  const [allItemList, setAllItemList] = useState<any[]>([]);
  const [allCasesList, setAllCasesList] = useState<any[]>([]);

  const [currentVerifierData, setCurrentVerifierData] =
    useState<any>(null);

  const [managerVerifierData, setManagerVerifierData] =
    useState<any>(null);

  const [filterData, setFilterData] =
    useState<FilterTypes>(initialFilters);

  const [filterErrors, setFilterErrors] =
    useState<FilterErrors>({});

  const [selectedDeleteItem, setSelectedDeleteItem] =
    useState<string | null>(null);

  const [showCasesModal, setShowCasesModal] =
    useState<boolean>(false);

  const [selectedRows, setSelectedRows] =
    useState<any[]>([]);

  const [pagination, setPagination] =
    useState<PaginationType>({
      current: 1,
      pageSize: 1000,
      total: 0,
    });

  const [pagination_cases, setPagination_cases] =
    useState<PaginationType>({
      current: 1,
      pageSize: 1000,
      total: 0,
    });

  // ================= API =================

  const {
    callApi: getAssignedCasesAPI,
    loading,
  } = useApi<any>(
    webservice_url.get_cases_by_verifierId,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const {
    callApi: getCasesByFilterAPI,
    loading: loading_getFilteredCases,
  } = useApi<any>(
    webservice_url.get_all_cases_by_filters,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const {
    callApi: deleteCaseApi,
    loading: loading_delete,
  } = useApi<any>("", {
    method: "DELETE",
    autoCall: false,
    handleGlobalError: false,
  });

  const {
    callApi: assignCasesAPI,
    loading: loading_assign,
  } = useApi<any>(
    webservice_url.assign_case_to_verifier,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  // ================= DELETE ASSIGNED CASE =================

  const handleDeleteProgram = async (id: string) => {
    try {
      if (!id) return;

      const res = await deleteCaseApi(
        undefined,
        `${webservice_url.delete_case_from_verifier}/${id}`,
      );

      if (res?.success) {
        toast.success(
          res?.message || "Case deleted successfully",
        );

        fetchData(
          pagination.current,
          pagination.pageSize,
        );
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      setSelectedDeleteItem(null);
    }
  };

  // ================= FETCH ASSIGNED CASES =================

  const fetchData = async (
    page: number = pagination.current,
    limit: number = pagination.pageSize,
  ) => {
    try {
      const res = await getAssignedCasesAPI({
        page,
        limit,
        member_id: verifierId ?? "",
      });

      if (res?.success) {
        const data = res?.data?.rows || [];

        setAllItemList(data);

        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize: limit,
          total:
            res?.data?.pagination?.totalRecords || 0,
        }));
      }
    } catch (error: any) {
      console.error(error?.response);
    }
  };

  useEffect(() => {
    if (!verifierId) return;

    fetchData(1, pagination.pageSize);
  }, [verifierId]);

  // ================= ASSIGNED CASE PAGINATION =================

  const handleTableChange = (pgConfig: any) => {
    if (
      pgConfig.current !== pagination.current ||
      pgConfig.pageSize !== pagination.pageSize
    ) {
      setPagination((prev) => ({
        ...prev,
        current: pgConfig.current,
        pageSize: pgConfig.pageSize,
      }));
    }
  };

  useEffect(() => {
    if (!verifierId) return;

    if (pagination.current === 1) return;

    fetchData(
      pagination.current,
      pagination.pageSize,
    );
  }, [
    pagination.current,
    pagination.pageSize,
  ]);

  // ================= FILTER CHANGE =================

  const handleFilterChange = (
    name: string,
    value: string[],
  ) => {
    setFilterData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };

      if (name === "organization") {
        updated.project = [];
        updated.location = [];
        updated.program = [];
        updated.department = [];
      }

      if (name === "project") {
        updated.location = [];
        updated.program = [];
        updated.department = [];
      }

      if (name === "location") {
        updated.program = [];
        updated.department = [];
      }

      if (name === "program") {
        updated.department = [];
      }

      return updated;
    });

    if (value?.length) {
      setFilterErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  // ================= FETCH CASES BY FILTER =================

  const fetchSearchData = async (
    current: number,
    limit: number,
    filters: FilterTypes = filterData,
  ) => {
    try {
      const res = await getCasesByFilterAPI({
        status: "",
        page: current,
        limit,

        org_ids: filters?.organization ?? [],
        project_ids: filters?.project ?? [],
        location_ids: filters?.location ?? [],
        program_ids: filters?.program ?? [],
        department_ids: filters?.department ?? [],

        manager_id:
          managerVerifierData?.managerId ?? "",
      });

      if (res?.success) {
        const data = res?.data?.rows || [];

        setAllCasesList(data);

        setPagination_cases({
          current,
          pageSize: limit,
          total:
            res?.data?.pagination?.totalRecords || 0,
        });
      }
    } catch (error: any) {
      console.error(error?.response);
    }
  };

  // ================= SEARCH =================

  const handleSearch = async () => {
    const errors: FilterErrors = {};

    if (!filterData?.organization?.length) {
      errors.organization = "Organization is required";
    }

    if (!filterData?.project?.length) {
      errors.project = "Account is required";
    }

    setFilterErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setPagination_cases((prev) => ({
      ...prev,
      current: 1,
    }));

    await fetchSearchData(
      1,
      pagination_cases.pageSize,
      filterData,
    );

    setSelectedRows([]);
    setShowCasesModal(true);
  };

  // ================= CLEAR FILTERS =================

  const handleClearAll = async () => {
    const clearedFilters: FilterTypes = {
      organization: [],
      project: [],
      location: [],
      program: [],
      department: [],
    };

    setFilterData(clearedFilters);
    setFilterErrors({});
    setAllCasesList([]);
    setSelectedRows([]);

    setPagination_cases((prev) => ({
      ...prev,
      current: 1,
      total: 0,
    }));
  };

  // ================= CASE TABLE PAGINATION =================

  const handleTableChange_cases = (
    pgConfig: any,
  ) => {
    if (
      pgConfig.current !== pagination_cases.current ||
      pgConfig.pageSize !== pagination_cases.pageSize
    ) {
      setPagination_cases((prev) => ({
        ...prev,
        current: pgConfig.current,
        pageSize: pgConfig.pageSize,
      }));
    }
  };

  useEffect(() => {
    if (!showCasesModal) return;

    if (
      pagination_cases.current === 1 &&
      pagination_cases.pageSize === 10
    ) {
      return;
    }

    fetchSearchData(
      pagination_cases.current,
      pagination_cases.pageSize,
      filterData,
    );
  }, [
    pagination_cases.current,
    pagination_cases.pageSize,
  ]);

  // ================= ASSIGN CASES =================

  const handleAssignCase = async () => {
    if (!selectedRows?.length) {
      toast.error("Please select at least one case.");
      return;
    }

    try {
      const payload = {
        member_id: verifierId,

        cases: selectedRows.map((item) => ({
          case_id: item.assignment_id,
          package_id: item.package_id,

          project_id:
            filterData?.project?.[0] ?? "",

          program_id:
            filterData?.program?.[0] ?? "",

          department_id:
            filterData?.department?.[0] ?? "",

          org_id:
            filterData?.organization?.[0] ?? "",
        })),
      };

      console.log("Assign Cases Payload:", payload);

      const response = await assignCasesAPI(payload);

      if (response?.success) {
        toast.success(
          response?.message ||
            "Cases assigned successfully.",
        );

        setShowCasesModal(false);
        setSelectedRows([]);

        fetchData(
          pagination.current,
          pagination.pageSize,
        );
      }
    } catch (error: any) {
      console.error(error);

      const errMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Something went wrong";

      toast.error(errMsg);
    }
  };

  // ================= ASSIGNED CASE COLUMNS =================

  const columns: ColumnsType<any> = [
    {
      title: "S.No",
      dataIndex: "sno",
      align: "center",
      render: (
        _text: string,
        _record: any,
        index: number,
      ) =>
        (pagination.current - 1) *
          pagination.pageSize +
        index +
        1,
      sorter: false,
    },

    {
      title: "Candidate Name",
      dataIndex: "full_name",
      onCell: () => cellWidth(180),
      sorter: (a, b) =>
        safeLocaleCompare(a, b, "full_name"),
    },

    {
      title: "Case Id",
      dataIndex: "case_id",
      onCell: () => cellWidth(190),
      sorter: (a, b) =>
        safeLocaleCompare(a, b, "case_id"),
    },

    {
      title: "Case Spoc",
      dataIndex: "case_spoc",
      onCell: () => cellWidth(140),
      sorter: (a, b) =>
        safeLocaleCompare(a, b, "case_spoc"),
    },

    {
      title: "Project",
      dataIndex: "project_name",
      key: "project_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.project_name ?? "").localeCompare(
          b.project_name ?? "",
        ),
    },

    {
      title: "Program",
      dataIndex: "program_name",
      key: "program_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.program_name ?? "").localeCompare(
          b.program_name ?? "",
        ),
    },

    {
      title: "Department",
      dataIndex: "department_name",
      key: "department_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.department_name ?? "").localeCompare(
          b.department_name ?? "",
        ),
    },

    {
      title: "Candidate Id",
      dataIndex: "hrms_id",
      onCell: () => cellWidth(160),
      sorter: (a, b) =>
        safeLocaleCompare(a, b, "hrms_id"),
    },

    {
      title: "Case Received Date",
      dataIndex: "case_received_date",
      onCell: () => cellWidth(200),
      render: (text: string) => {
        const date = formattedDate(text) || "-";
        return <div>{date}</div>;
      },
      sorter: (a: any, b: any) =>
        parseDate(
          a.case_received_date,
        ).getTime() -
        parseDate(
          b.case_received_date,
        ).getTime(),
    },

    {
      title: "Due Date",
      dataIndex: "due_date",
      onCell: () => cellWidth(140),
      render: (text: string) => {
        const date = formattedDate(text) || "-";
        return <div>{date}</div>;
      },
      sorter: (a: any, b: any) =>
        parseDate(a.due_date).getTime() -
        parseDate(b.due_date).getTime(),
    },

    {
      title: "Action",
      dataIndex: "",
      align: "center",

      render: (_text: string, record: any) => {
        const id =
          record?.member_case_map_id ?? "";

        return (
          <button
            type="button"
            className="btn btn-sm btn-danger"
            disabled={loading_delete}
            onClick={() => {
              setSelectedDeleteItem(id);
              handleDeleteProgram(id);
            }}
          >
            {selectedDeleteItem === id ? (
              "Deleting..."
            ) : (
              <>
                <i className="ti ti-trash" /> Delete
              </>
            )}
          </button>
        );
      },
    },
  ];

  // ================= AVAILABLE CASE COLUMNS =================

  const columns_cases: ColumnsType<any> = [
    {
      title: "S.No",
      dataIndex: "sno",
      align: "center",
      render: (
        _text: string,
        _record: any,
        index: number,
      ) =>
        (pagination_cases.current - 1) *
          pagination_cases.pageSize +
        index +
        1,
      sorter: false,
    },

    {
      title: "Candidate Name",
      dataIndex: "applicant_name",
      onCell: () => cellWidth(200),
      sorter: (a, b) =>
        safeLocaleCompare(
          a,
          b,
          "applicant_name",
        ),
    },

    {
      title: "Case Id",
      dataIndex: "case_id",
      onCell: () => cellWidth(190),
      sorter: (a, b) =>
        safeLocaleCompare(a, b, "case_id"),
    },

    {
      title: "Case Spoc",
      dataIndex: "case_spoc",
      onCell: () => cellWidth(140),
      sorter: (a, b) =>
        safeLocaleCompare(
          a,
          b,
          "case_spoc",
        ),
    },

    {
      title: "Project",
      dataIndex: "project_name",
      key: "project_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.project_name ?? "").localeCompare(
          b.project_name ?? "",
        ),
    },

    {
      title: "Program",
      dataIndex: "program_name",
      key: "program_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.program_name ?? "").localeCompare(
          b.program_name ?? "",
        ),
    },

    {
      title: "Department",
      dataIndex: "department_name",
      key: "department_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.department_name ?? "").localeCompare(
          b.department_name ?? "",
        ),
    },

    {
      title: "Candidate Id",
      dataIndex: "hrms_id",
      onCell: () => cellWidth(160),
      sorter: (a, b) =>
        safeLocaleCompare(a, b, "hrms_id"),
    },

    {
      title: "Total checks",
      dataIndex: "total_checks",
      onCell: () => cellWidth(160),
      sorter: (a, b) =>
        safeLocaleCompare(
          a,
          b,
          "total_checks",
        ),
    },

    {
      title: "Stopped",
      dataIndex: "stopped",
      onCell: () => cellWidth(160),
      sorter: (a, b) =>
        safeLocaleCompare(
          a,
          b,
          "stopped",
        ),
    },

    {
      title: "Due Date",
      dataIndex: "due_date",
      onCell: () => cellWidth(160),
      sorter: (a, b) =>
        safeLocaleCompare(
          a,
          b,
          "due_date",
        ),
      render: (text) =>
        formattedDate(text),
    },

    {
      title: "Case Received Date",
      dataIndex: "case_received_date",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) =>
        parseDate(
          a.case_received_date,
        ).getTime() -
        parseDate(
          b.case_received_date,
        ).getTime(),
      render: (text) =>
        formattedDate(text),
    },
  ];

  // ================= LOAD VERIFIER / MANAGER =================

  useEffect(() => {
    const storedData = getLocalStorage(
      "admin_selected_verifier",
    );

    if (!storedData) return;

    setCurrentVerifierData(storedData);

    setManagerVerifierData({
      name: storedData?.managerName,
      email: storedData?.managerEmail,
      mobile: storedData?.managerNumber,
      managerId: storedData?.managerId,
    });
  }, []);

  // ================= UI =================

  return (
    <>
      <div className="page-wrapper">
        <div className="content">

          <PageBreadcrumb
            title="Manage Verifier Cases"
            breadcrumbs={breadcrumbItem}
            showBackButton={true}
            backRoute={all_routes.admin_manageVerifier}
          />

          {/* Verifier Details */}
          <div className="section-title">
            <span className="section-bar"></span>
            <h4>Verifier Details</h4>
          </div>

          <UserDetailsSection
            userDetails={currentVerifierData}
          />

          {/* Manager Details */}
          <div className="section-title">
            <span className="section-bar"></span>
            <h4>Manager Details</h4>
          </div>

          <UserDetailsSection
            userDetails={managerVerifierData}
          />

          <div className="pt-4"></div>

          {/* Filters */}

          <DynamicSingleSelect
            fields={[
              "organization",
              "project",
              "location",
              "program",
              "department",
            ]}
            filterData={filterData}
            onFilterDataChange={(
              name,
              value,
              _labels,
            ) => {
              handleFilterChange(
                name,
                value,
              );
            }}
            parentError={filterErrors}
            showButton={true}
            buttonText={
              loading_getFilteredCases
                ? "Searching..."
                : "Search"
            }
            onButtonClick={handleSearch}
            disableButton={
              loading_getFilteredCases ||
              loading ||
              showCasesModal
            }
            onClearAll={handleClearAll}
          />

          {/* Assigned Cases Table */}

          <div
            className="card mt-3"
            style={{
              minHeight: "250px",
            }}
          >
            {loading ? (
              <Loading />
            ) : (
              <Datatable
                dataSource={allItemList}
                columns={columns}
                Selection={false}
                rowKey="member_case_map_id"
                pagination={pagination}
                handleTableChange={
                  handleTableChange
                }
              />
            )}
          </div>

          {/* Assign Cases Modal */}

          {showCasesModal && (
            <div>
              {/* Backdrop */}
              <div className="modal-backdrop fade show"></div>

              {/* Modal */}
              <div
                className="modal fade show d-block"
                tabIndex={-1}
                role="dialog"
                aria-labelledby="modalTitleId"
                aria-modal="true"
              >
                <div
                  className="modal-dialog modal-dialog-scrollable modal-dialog-centered modal-xl"
                  role="document"
                  style={{
                    width: "95%",
                    minWidth: "700px",
                    maxWidth: "800px",
                  }}
                >
                  <div className="modal-content w-100">

                    {/* Modal Header */}

                    <div className="modal-header">
                      <div
                        className="modal-title"
                        id="modalTitleId"
                        style={{
                          fontSize: "16px",
                        }}
                      >
                        Assign Cases
                      </div>

                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => {
                          setShowCasesModal(false);
                          setSelectedRows([]);
                        }}
                      />
                    </div>

                    {/* Modal Body */}

                    <div className="modal-body overflow-y">

                      <div className="text-end">
                        <button
                          type="button"
                          className="btn btn-tertiary py-1"
                          disabled={
                            !selectedRows?.length ||
                            loading_assign
                          }
                          onClick={
                            handleAssignCase
                          }
                        >
                          {loading_assign
                            ? "Assigning..."
                            : "+ Assign Cases"}
                        </button>
                      </div>

                      {/* Cases Table */}

                      <div className="card mt-3">
                        {loading_getFilteredCases ? (
                          <Loading />
                        ) : (
                          <Datatable
                            rowKey="case_id"
                            dataSource={
                              allCasesList
                            }
                            columns={
                              columns_cases
                            }
                            Selection={true}
                            onSelectionChange={(
                              rows,
                            ) => {
                              setSelectedRows(
                                rows,
                              );
                            }}
                            pagination={
                              pagination_cases
                            }
                            handleTableChange={
                              handleTableChange_cases
                            }
                          />
                        )}
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>
        {`
          .section-title {
            display: flex;
            align-items: center;
            gap: 12px;
            margin: 28px 0 18px;
          }

          .section-bar {
            width: 5px;
            height: 24px;
            background: #3f3d99;
            border-radius: 10px;
          }

          .section-title h4 {
            margin: 0;
            font-size: 24px;
            font-weight: 700;
            color: #243b6b;
            letter-spacing: 0.3px;
          }
        `}
      </style>
    </>
  );
};

export default Admin_ManageVerifierCases;